import { ConversationParticipantView } from '@mark43/cad-api';
import { toJS } from 'mobx';
import { NEW_CONVERSATION_ID } from './constants';
import { ConversationKeyObject } from './types';

export class ConversationKey {
    conversationId: number;
    participantIdentity?: ConversationParticipantView;
    isBookmarkedConversation: boolean;

    constructor(
        conversationId: number,
        participantIdentity?: ConversationParticipantView,
        isBookmarkedConversation?: boolean
    ) {
        this.conversationId = conversationId;
        this.participantIdentity = participantIdentity;
        this.isBookmarkedConversation = !!isBookmarkedConversation;
    }

    equals(otherConversationKey?: ConversationKey): boolean {
        return this.toString() === otherConversationKey?.toString();
    }

    toString({ includeBookmarkedString }: { includeBookmarkedString?: boolean } = {}): string {
        const participantString =
            this.participantIdentity === undefined
                ? ''
                : `-participantId-${this.participantIdentity.id}-participantType-${this.participantIdentity.entityType}`;

        // * If includeBookmarkedString is not specified, bookmarkedString will be added to conversationKeys for
        //   bookmarked conversations only.
        // * If includeBookmarkedString is specified, bookmarkedString will be added to conversationKeys if
        //   includeBookmarkedString is true, and won't be if includeBookmarkedString is false.
        //
        // The reason for this is to be able to convert a conversationkey pointed towards a conversation to a
        // conversationkey pointed towards the bookmarked conversation (of the same conversationId). This is
        // used when bookmarking a message in conversation view, we need to be able to reference the same
        // conversation in bookmarked converation view also.
        //
        const finalIncludeBookmarkedString =
            includeBookmarkedString === undefined
                ? this.isBookmarkedConversation
                : includeBookmarkedString;
        const bookmarkedString = finalIncludeBookmarkedString ? '-bookmarks' : '';

        return `conversationId-${this.conversationId}${participantString}${bookmarkedString}`;
    }

    isNewConversationKey(): boolean {
        return this.conversationId === NEW_CONVERSATION_ID;
    }

    isBookmarkedConversationKey(): boolean {
        return this.isBookmarkedConversation;
    }

    toObject(): ConversationKeyObject {
        return {
            conversationId: this.conversationId,
            participantIdentity: toJS(this.participantIdentity),
            isBookmarkedConversation: this.isBookmarkedConversation,
        };
    }

    static fromObject(conversationKeyObject: ConversationKeyObject): ConversationKey {
        const { conversationId, participantIdentity, isBookmarkedConversation } =
            conversationKeyObject;
        return new ConversationKey(conversationId, participantIdentity, isBookmarkedConversation);
    }
}
